// Structured role input (company roles and curator-posted roles). Zod first;
// the API validates with this and both dashboards infer their form types.
import { z } from 'zod';

import { EMPLOYMENT_TYPES, SENIORITIES } from './enums.ts';
import { regionCodeSchema } from './regions.ts';
import { currencySchema, timezoneSchema } from './candidate.ts';

const roleSkillInputSchema = z.object({
  skillId: z.uuid(),
  required: z.boolean(),
});

export const roleInputSchema = z
  .object({
    title: z.string().trim().min(3).max(120),
    // Required, with no "not specified" choice: every role has a type,
    // industry, seniority and employment type.
    roleTypeId: z.uuid({ error: 'Choose a role type' }),
    industryId: z.uuid({ error: 'Choose an industry' }),
    seniority: z.enum(SENIORITIES, { error: 'Choose a seniority' }),
    employmentType: z.enum(EMPLOYMENT_TYPES, { error: 'Choose an employment type' }),
    salaryMin: z.number().int().positive().max(10_000_000).nullable(),
    salaryMax: z.number().int().positive().max(10_000_000).nullable(),
    currency: currencySchema.nullable(),
    /** Hours of overlap the team needs with the candidate's working day. */
    overlapHours: z.number().int().min(0).max(12).nullable(),
    /** IANA zone the team works in; required when overlapHours is set. */
    timezone: timezoneSchema.nullable(),
    /** Our own words (or the company's), never a pasted description. */
    summary: z.string().trim().min(20).max(1200).nullable(),
    skills: z.array(roleSkillInputSchema).max(30),
    /** Empty = worldwide. */
    regions: z.array(regionCodeSchema).max(30),
  })
  .superRefine((r, ctx) => {
    if (r.salaryMin !== null && r.salaryMax !== null && r.salaryMin > r.salaryMax) {
      ctx.addIssue({ code: 'custom', path: ['salaryMax'], message: 'Max must be ≥ min' });
    }
    if ((r.salaryMin !== null || r.salaryMax !== null) && r.currency === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['currency'],
        message: 'Currency required with salary',
      });
    }
    const seen = new Set<string>();
    for (const s of r.skills) {
      if (seen.has(s.skillId)) {
        ctx.addIssue({ code: 'custom', path: ['skills'], message: 'Duplicate skill' });
        break;
      }
      seen.add(s.skillId);
    }
    if (new Set(r.regions).size !== r.regions.length) {
      ctx.addIssue({ code: 'custom', path: ['regions'], message: 'Duplicate region' });
    }
  });
export type RoleInput = z.infer<typeof roleInputSchema>;

// Curated roles add provenance (PRODUCT.md "Curated-role posting rules").
export const curatedRoleInputSchema = roleInputSchema.safeExtend({
  sourceUrl: z
    .url({ protocol: /^https$/, hostname: z.regexes.domain })
    .max(500)
    .refine(
      (u) => !/(linkedin|indeed|glassdoor|weworkremotely|remotive|himalayas|remoteok)\./i.test(u),
      { message: 'Link to the original posting on the company site, not a job board' },
    ),
  sourceCompanyName: z.string().trim().min(1).max(120),
  summary: z.string().trim().min(20).max(1200),
});
export type CuratedRoleInput = z.infer<typeof curatedRoleInputSchema>;

// A curator re-checks each open curated listing at least this often; older
// ones show under "Needs re-check" and in the daily freshness count.
export const CURATED_FRESHNESS_DAYS = 14;
